import { useState } from 'react'
import ComparisonFrame from './ComparisonFrame'
import KpiDeltaCompare from './compareVariants/KpiDeltaCompare'
import GroupedBarCompare from './compareVariants/GroupedBarCompare'
import formatPeriodLabel from '../../helpers/compareHelpers/formatPeriodLabel'
import mergeCategorySeries from '../../helpers/compareHelpers/mergeCategorySeries'

// AI-CONTEXT: See docs/features/compare-two-periods.md
// Compare view for a single indicator (question) across two periods. It always
// renders the small-multiples frame and harvests each period's compare payload
// (reported by the chart's own data component — NumberData/MultipleData — so
// the merged view never diverges from the chart). It then upgrades:
//   - both single-moment number  → KpiDeltaCompare ("A vs B + Δ")
//   - both single-moment multiple → GroupedBarCompare (two bars per option)
//
// The frame stays mounted while a merged view shows (hidden via
// `.compare-harvester`, display:none) so harvesting keeps running and the mode
// can't oscillate (unmounting the frame would drop the payloads → flip back →
// loop). Anything else (multi-moment, scale, matrix, …) stays on small
// multiples.

/**
 * @param {object} props
 * @param {{start: string|null, end: string|null}|null} props.rangeA - Primary period
 * @param {{start: string|null, end: string|null}|null} props.rangeB - Compare period
 * @param {(range: {start: string|null, end: string|null}|null, side: 'A'|'B', onCompareData: (payload: object) => void) => React.ReactNode} props.renderViz - Renders one period's chart-only indicator, wired to report its KPI payload
 * @returns {JSX.Element}
 */
const IndicatorCompareView = ({ rangeA, rangeB, renderViz }) => {
    const [payloadA, setPayloadA] = useState(null)
    const [payloadB, setPayloadB] = useState(null)

    const bothEligible = Boolean(payloadA?.eligible && payloadB?.eligible)
    const sameKind = payloadA?.kind === payloadB?.kind
    const bothKpi = bothEligible && sameKind && payloadA.kind === 'kpi'
    const bothGrouped = bothEligible && sameKind && payloadA.kind === 'grouped'
    const merged = bothKpi || bothGrouped

    const labelA = formatPeriodLabel(rangeA)
    const labelB = formatPeriodLabel(rangeB)

    return (
        <>
            <div className={merged ? 'compare-harvester' : ''}>
                <ComparisonFrame
                    rangeA={rangeA}
                    rangeB={rangeB}
                    renderViz={(range, side) => renderViz(range, side, side === 'A' ? setPayloadA : setPayloadB)}
                />
            </div>
            {bothKpi && (
                <KpiDeltaCompare
                    valueA={payloadA.value}
                    valueB={payloadB.value}
                    labelA={labelA}
                    labelB={labelB}
                    /* 1 decimal, matching NumberMomentVisualisation, so toggling
                       between the merged and small-multiples view shows the same
                       number. */
                    formatValue={(n) => n.toFixed(1)}
                />
            )}
            {bothGrouped && (
                <GroupedBarCompare
                    data={mergeCategorySeries(payloadA.series, payloadB.series)}
                    labelA={labelA}
                    labelB={labelB}
                />
            )}
        </>
    )
}

export default IndicatorCompareView
